import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  TextField,
  Typography,
  styled
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import Swal from 'sweetalert2';
import { AuthService } from 'src/services/AuthService';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(3)
  }
}));

function TopUpSmsDialog({
  open,
  onClose,
  userId,
  smsLenzSenderID = '',
  onSuccess
}: {
  open: boolean;
  onClose: () => void;
  userId: string;
  smsLenzSenderID?: string;
  onSuccess?: (smsLenzSenderID: string) => void;
}) {
  const [loading, setLoading] = useState(false);

  const validationSchema = yup.object({
    amount: yup
      .number()
      .typeError('Enter a valid amount')
      .required('Amount is required')
      .min(100, 'Minimum top-up amount is 100'),
    smsLenzSenderID: yup.string().trim().nullable()
  });

  const formik = useFormik({
    initialValues: {
      amount: '',
      smsLenzSenderID: ''
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);

      try {
        const senderId = values.smsLenzSenderID?.trim() || '';
        const data: {
          userId: string;
          payAmount: string | number;
          smsLenzSenderID?: string;
        } = {
          userId: userId,
          payAmount: values.amount
        };

        if (senderId) {
          data.smsLenzSenderID = senderId;
        }

        AuthService.TopUpSms(data).then((res) => {
          if (res.success) {
            if (senderId) {
              onSuccess?.(senderId);
            }
            onClose();
            formik.resetForm({
              values: { amount: '', smsLenzSenderID: senderId || '' }
            });
            Swal.fire('Shop Updated', 'Successfully', 'success');
          } else {
            Swal.fire({
              icon: 'error',
              title: 'Top-up failed',
              confirmButtonColor: '#FD7F00'
            });
          }
        });
      } catch (error) {
        Swal.fire('Error', 'Top-up failed', 'error');
      } finally {
        setLoading(false);
      }
    }
  });

  useEffect(() => {
    if (open) {
      formik.resetForm({
        values: {
          amount: '',
          smsLenzSenderID: smsLenzSenderID || ''
        }
      });
    }
  }, [open, smsLenzSenderID]);

  return (
    <BootstrapDialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <Box sx={{ p: 2, position: 'relative' }}>
        <Typography variant="h4" fontWeight="bold">
          Top-Up SMS
        </Typography>

        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress />
          </Box>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Typography mb={1}>Enter Amount (LKR)</Typography>

            <TextField
              fullWidth
              name="amount"
              type="number"
              placeholder="100 / 200 / 500"
              value={formik.values.amount}
              onChange={formik.handleChange}
              error={Boolean(formik.touched.amount && formik.errors.amount)}
              helperText={formik.touched.amount && formik.errors.amount}
            />

            <Typography mb={1} mt={2.5}>
              SMS Lenz Sender ID (optional)
            </Typography>

            <TextField
              fullWidth
              name="smsLenzSenderID"
              placeholder="Enter SMS Lenz Sender ID"
              value={formik.values.smsLenzSenderID}
              onChange={formik.handleChange}
            />

            <Box mt={3}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
              >
                Top-Up SMS
              </Button>
            </Box>
          </form>
        )}
      </DialogContent>
    </BootstrapDialog>
  );
}

export default TopUpSmsDialog;
